export const SigFieldKeys = {
  SIG: 'sig',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  STATUS: 'status',
  DIRECTION: 'direction',
  DIRECTION_COUNT: 'directionCount',
  APPROVED_DRUG_COUNT: 'approvedDrugCount',
};

export const SigStatus = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

// What deactivating a sig does to its drug approvals, said on screen because nothing else says it.
// Deactivating cascades onto the sig's directions but deliberately leaves catalog_approved_sigs
// alone, so the approvals outlive it and come back untouched when the sig is activated again.
export const SigStatusChangeCopy = {
  APPROVALS_STAY_ON_RETIRE: 'will stay active. Deactivating a sig does not withdraw them.',
  APPROVALS_RETURN_ON_RESTORE: 'will come back with it, exactly as they were.',
};

// Narrows an association list inside the dialog. The dialog already holds the rows, so this filters
// in place with no extra request.
export const AssociationStatusFilter = {
  ALL: 'ALL',
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE',
};

export const AssociationStatusFilterLabel = {
  ALL: 'All',
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

/**
 * The only two words the screen uses for an association's state.
 *
 * The database keeps the row and flips `is_active`, and the backend calls that "retired" — but this
 * screen is read by pharmacists, not by whoever wrote the migration. Active and Inactive are the
 * two states they already think in, so the internal word stays in the code and out of the UI.
 */
export const AssociationStatusLabel = {
  ACTIVE: 'Active',
  INACTIVE: 'Inactive',
};

// What a staged change will do once committed. Named for what the user chose rather than for the
// column it writes: "set inactive" is the decision, `is_active = false` is the consequence.
export const SigDraftAction = {
  ADD: 'ADD',
  SET_INACTIVE: 'SET_INACTIVE',
  SET_ACTIVE: 'SET_ACTIVE',
  EDIT_SIG: 'EDIT_SIG',
};

export const SigDraftActionLabel = {
  ADD: 'Add',
  SET_INACTIVE: 'Set inactive',
  SET_ACTIVE: 'Set active',
  EDIT_SIG: 'Edit sig',
};

// What kind of thing a pending change is about.
export const SigDraftItemLabel = {
  SIG: 'Sig',
  DIRECTION: 'Direction',
  CATALOG_ITEM: 'Catalog item',
};

// Nothing in the dialog is written until Commit changes, and that is not obvious from a list of
// rows that already look like saved data.
export const PENDING_CHANGES_HELPER_TEXT = 'Changes are only applied after you commit them.';

/**
 * How long a sig and a direction may be. Mirrors utils/sigLimits.ts on the backend, and every place
 * that accepts either one reads from here.
 *
 * They were separate literals — the Add Sig form, the inline sig editor, the add-direction field
 * and the sig search box each decided for themselves, so some enforced a maximum and some did not,
 * and one caller passed a third number entirely. One definition, so a length the screen accepts is
 * a length the server accepts.
 *
 * This governs how long the stored text may be. It deliberately does NOT govern the filter fields'
 * minimum search length, which happens to be the same number today but is a different question.
 *
 * DIRECTION_MAX is the column: `approved_direction.direction` is varchar(510). SIG_MAX is a product
 * limit and deliberately lower, which is what makes the resolve screen's "Create Sig From
 * Direction" a gate rather than a write that fails.
 */
export const SigLengthLimits = {
  SIG_MIN: 3,
  SIG_MAX: 180,
  DIRECTION_MIN: 3,
  DIRECTION_MAX: 510,
};

// Shown where a catalog item has no strength, form or pack size recorded. A blank cell reads as a
// rendering fault; NA says the catalog does not have it.
export const NOT_AVAILABLE = 'NA';

/**
 * The sig id the draft is held under while a brand new sig is being composed.
 *
 * The create and edit flows are the same dialog and the same staged draft, and the draft is keyed
 * on the sig it belongs to. A sig that does not exist yet has no id, so it needs one of its own —
 * without it, `startSigDraft` cannot tell "compose a new sig" apart from "no draft at all" and a
 * half-composed sig would be silently reused or silently dropped. Real sig ids are uuids, so this
 * cannot collide with one.
 */
export const NEW_SIG_DRAFT_ID = 'new-sig';
